import { useState, useEffect, useRef } from "react";
import CircleInfo from "../design-system/icons/CircleInfo.jsx";
import StarLines from "../design-system/icons/StarLines.jsx";
import useAuthedApi from "../hooks/useAuthedApi";
import Alert from "../design-system/alert/Alert.jsx";
import Drawer from "./commons/Drawer.jsx";

// ── helpers ───────────────────────────────────────────────────────────────────

function fmt(n, d = 2) {
  const v = parseFloat(n);
  return isNaN(v) || !isFinite(v) ? "—" : v.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
}

const LEVERAGE_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

// ── Stepper ───────────────────────────────────────────────────────────────────

function Stepper({ step }) {
  const labels = ["Trade Config", "Bot Settings", "Confirmation"];
  return (
    <div className="flex items-center gap-0 mb-5">
      {labels.map((label, i) => {
        const n = i + 1;
        const done = step > n;
        const active = step === n;
        return (
          <div key={n} className="flex items-center flex-1 min-w-0">
            <div className="flex items-center gap-1.5 shrink-0">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0
                ${done ? "bg-[#09421E] text-white" : active ? "bg-[#1264CE] text-white" : "bg-white text-[#7B7B7B] border border-[#EAEAEA]"}`}>
                {done ? "✓" : n}
              </div>
              <span className="text-[11px] font-medium whitespace-nowrap text-[#1C1C1C]">
                {label}
              </span>
            </div>
            {i < 2 && (
              <div className={`flex-1 h-[1.5px] mx-2 ${step > n ? "bg-[#0F6D32]" : "bg-gray-200"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── InfoIcon / FieldLabel ─────────────────────────────────────────────────────

function InfoIcon() {
  return <CircleInfo className="w-3.5 h-3.5 text-[#A4A9AC] inline ml-1" />;
}

function FieldLabel({ children, hint, error }) {
  return (
    <label className={`text-sm font-semibold mb-1 block ${error ? "text-red-500" : "text-[#1C1C1C]"}`}>
      {children}
      {hint && <InfoIcon />}
    </label>
  );
}

// ── SuffixInput ───────────────────────────────────────────────────────────────

function SuffixInput({ suffix, value, onChange, type = "number", min, max, step, placeholder, readOnly, error }) {
  return (
    <div className={`flex items-center border rounded-xl overflow-hidden bg-white focus-within:ring-2 ${
      error ? "border-red-400 focus-within:ring-red-300" : "border-gray-200 focus-within:ring-[#5B3DF6]"
    } ${readOnly ? "bg-[#F2F4F8]" : ""}`}>
      <input
        type={type}
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={onChange}
        placeholder={placeholder || "000"}
        readOnly={readOnly}
        className="flex-1 min-w-0 bg-transparent px-3 py-2.5 text-sm text-[#21272A] outline-none"
      />
      {suffix && (
        <span className="px-3 py-2.5 text-xs text-[#697077] font-medium shrink-0 border-l border-gray-100">
          {suffix}
        </span>
      )}
    </div>
  );
}

// ── CoinAvatar ────────────────────────────────────────────────────────────────

function CoinAvatar({ symbol }) {
  const initials = (symbol || "?").slice(0, 3).toUpperCase();
  return (
    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-[9px] font-bold text-white shrink-0">
      {initials}
    </div>
  );
}

// ── BorrowCard — one side's wallet balance + borrowable + rate (step 1) ───────

function BorrowCard({ side, symbol, walletBalance, availableLabel, availableValue, hourlyRate, apr, loading, error, unavailable, zeroBalance, zeroBalanceMessage }) {
  return (
    <div className="border border-gray-200 rounded-xl p-3 space-y-2.5">
      <div className="flex items-center gap-2.5">
        <CoinAvatar symbol={symbol} />
        <div>
          <p className="text-[10px] text-[#697077]">Wallet Balance</p>
          <p className="text-sm font-semibold text-[#21272A]">
            {loading ? "Loading…" : walletBalance}
          </p>
        </div>
      </div>

      <div>
        <p className="text-[10px] text-[#697077] mb-0.5">
          {side} - Available to borrow <InfoIcon />
        </p>
        {error ? (
          <p className="text-xs text-red-500">{error}</p>
        ) : (
          <p className="text-sm font-semibold text-[#21272A] break-words">
            {loading ? "Loading…" : availableValue}
          </p>
        )}
      </div>

      {/* Error: zero balance — e.g. SHORT needs to actually hold the base asset.
          Takes priority over "pair unavailable" (see shortUnavailable in Step1):
          a 0 balance is the real, relevant reason, not an OKX pair quirk. */}
      {!loading && !error && zeroBalance && (
        <Alert
          status="error"
          alertStyle="lighter"
          size="small"
          dismissible={false}
          message={zeroBalanceMessage}
        />
      )}

      {/* Warning: this side's borrowable is unreliably reporting 0 (OKX quirk) */}
      {!loading && !error && !zeroBalance && unavailable && (
        <Alert
          status="warning"
          alertStyle="lighter"
          size="small"
          dismissible={false}
          message="This pair isn't available for Leverage"
        />
      )}

      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-[#302571] text-[10px] text-white font-medium">
        Interest rate:{" "}
        {!loading && !error && hourlyRate != null
          ? `${(hourlyRate * 24 * 100).toFixed(6)}%/day (${(apr || 0).toFixed(2)}% APR)`
          : "-"}
      </span>
    </div>
  );
}

// ── Step 1 — Trade Config ─────────────────────────────────────────────────────

function Step1({ s1, setS1, tradable, longLimit, shortLimit, loadingLong, loadingShort, longError, shortError }) {
  const { authedGet } = useAuthedApi();
  const [pairs, setPairs] = useState([]);
  const set = (k) => (e) => setS1((p) => ({ ...p, [k]: e.target.value }));
  const baseCcy = longLimit?.base_ccy || shortLimit?.base_ccy || s1.symbol.toUpperCase();

  useEffect(() => {
    authedGet("/api/borrow/pairs/")
      .then((r) => setPairs(r.data.pairs || []))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const longAvail = parseFloat(longLimit?.available_loan_usdt || 0);
  const shortAvailBase = parseFloat(shortLimit?.available_loan || 0);
  const shortPrice = parseFloat(shortLimit?.price || longLimit?.price || 0);
  const shortBalanceBase = parseFloat(shortLimit?.balance_base || 0);
  // LS needs both sides to actually work — OKX's borrowable=0 quirk is
  // surfaced independently per side, same reasoning as the single-side modal.
  // SHORT is an exception: shorting requires actually holding the base asset,
  // so when balance is also 0, that's the real/relevant reason (shortZeroBalance
  // below), not an OKX pair quirk — don't flag it as pairUnavailable too.
  const longUnavailable = !!(longLimit && !longError && longAvail === 0);
  const shortZeroBalance = !!(shortLimit && !shortError && shortBalanceBase === 0);
  const shortUnavailable = !!(shortLimit && !shortError && shortAvailBase === 0 && !shortZeroBalance);

  return (
    <div className="space-y-4">
      {/* Exchange */}
      <div>
        <FieldLabel hint>Exchange</FieldLabel>
        <div className="relative">
          <img src="/icon_okx.svg" alt="OKX" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full pointer-events-none" />
          <select
            className="w-full border border-gray-200 rounded-xl pl-10 pr-3 py-2.5 text-sm text-[#21272A] outline-none focus:ring-2 focus:ring-[#5B3DF6] bg-white"
            value={s1.credentialId}
            onChange={set("credentialId")}
          >
            <option value="">Select Exchange</option>
            {tradable.map((a) => (
              <option key={a.id} value={a.id}>{a.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Borrowable */}
      <div>
        <p className="text-xs font-semibold text-[#21272A] mb-3">Borrowable</p>

        <div className="mb-3">
          <FieldLabel hint>Leverage</FieldLabel>
          <select
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-[#21272A] outline-none focus:ring-2 focus:ring-[#5B3DF6] bg-white"
            value={s1.leverage}
            onChange={set("leverage")}
          >
            <option value="">Select leverage</option>
            {LEVERAGE_OPTIONS.map((l) => (
              <option key={l} value={l}>x{l}</option>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <FieldLabel hint>Trading Pair</FieldLabel>
          <select
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-[#21272A] outline-none focus:ring-2 focus:ring-[#5B3DF6] bg-white"
            value={s1.symbol}
            onChange={set("symbol")}
          >
            <option value="">Select Trading pair</option>
            {pairs.map((p) => (
              <option key={p} value={p.split("/")[0]}>{p}</option>
            ))}
          </select>
        </div>

        {/* One card per borrowed currency — LONG borrows USDT, SHORT borrows the base coin */}
        <div className="space-y-2">
          <BorrowCard
            side="LONG"
            symbol="USDT"
            walletBalance={longLimit ? `${fmt(parseFloat(longLimit.balance_usdt || 0))} USDT` : "-"}
            availableValue={longLimit ? `${fmt(longAvail)} USDT` : "-"}
            hourlyRate={longLimit ? parseFloat(longLimit.hourly_rate || 0) : null}
            apr={parseFloat(longLimit?.apr || 0)}
            loading={loadingLong}
            error={longError}
            unavailable={longUnavailable}
          />
          <BorrowCard
            side="SHORT"
            symbol={baseCcy}
            walletBalance={shortLimit ? `${fmt(parseFloat(shortLimit.balance_base || 0), 4)} ${baseCcy}` : "-"}
            availableValue={
              shortLimit
                ? `${fmt(shortAvailBase, 2)} ${baseCcy}${shortPrice > 0 ? ` (=${fmt(shortAvailBase * shortPrice)} USDT)` : ""}`
                : "-"
            }
            hourlyRate={shortLimit ? parseFloat(shortLimit.hourly_rate || 0) : null}
            apr={parseFloat(shortLimit?.apr || 0)}
            loading={loadingShort}
            error={shortError}
            unavailable={shortUnavailable}
            zeroBalance={shortZeroBalance}
            zeroBalanceMessage={`You have 0.00 ${baseCcy} balance in your exchange account. To run the bot, please add fund (${baseCcy}) to your exchange account.`}
          />
        </div>
      </div>
    </div>
  );
}

// ── Capital split side card (step 2) ─────────────────────────────────────────

function SplitColumn({ side, capitalLabel, equivalent, totalLiability, dailyInterest, maxSteps }) {
  return (
    <div className="flex flex-col">
      {/* inset header pill, with breathing room before the capital figure */}
      <div className="m-2 mb-3 bg-[#F6F7F8] rounded-lg py-1.5 text-center">
        <span className="text-[11px] font-semibold text-[#7B7B7B] tracking-wide">{side}</span>
      </div>
      {/* fixed height only when side-by-side, so both columns' dividers line up */}
      <div className="px-3 pb-3 text-center tablet:min-h-[52px]">
        <p className="text-base font-bold text-[#1C1C1C]">{capitalLabel}</p>
        {equivalent && <p className="text-[10px] text-[#9CA3AF] mt-0.5">{equivalent}</p>}
      </div>
      <div className="px-3 py-2.5 border-t border-gray-100 space-y-2.5">
        <div>
          <p className="text-[10px] text-[#7B7B7B] uppercase tracking-wide">Total Liability</p>
          <p className="text-sm font-semibold text-[#21272A]">{totalLiability}</p>
        </div>
        <div className="bg-[#EAF2FD] rounded-lg px-2 py-1">
          <p className="text-[10px] text-[#1264CE]">Est. Daily Interest : {dailyInterest}</p>
        </div>
        <SuffixInput suffix={<>Max Steps <InfoIcon /></>} value={maxSteps} readOnly />
      </div>
    </div>
  );
}

// ── Step 2 — Bot Settings ─────────────────────────────────────────────────────

function Step2({ s1, s2, setS2, longLimit, shortLimit }) {
  const set = (k) => (e) => setS2((p) => ({ ...p, [k]: e.target.value }));

  const price = parseFloat(shortLimit?.price || longLimit?.price || 0);
  const baseCcy = longLimit?.base_ccy || shortLimit?.base_ccy || s1.symbol.toUpperCase();

  const capitalNum = parseFloat(s2.tradingCapital) || 0;
  const tradeSize = parseFloat(s2.tradeSize) || 0;

  const availUsdt = parseFloat(longLimit?.available_loan_usdt || 0);
  const availShortBase = parseFloat(shortLimit?.available_loan || 0);
  // Combined borrowable in USDT: long (USDT) + short (base × price)
  const totalBorrowUsdt = availUsdt + (price > 0 ? availShortBase * price : 0);
  const sliderPct = totalBorrowUsdt > 0 ? Math.round((capitalNum / totalBorrowUsdt) * 100) : 0;
  const handleSlider = (e) => {
    const pct = Number(e.target.value);
    setS2((p) => ({ ...p, tradingCapital: ((totalBorrowUsdt * pct) / 100).toFixed(2) }));
  };

  // Capital is split 50/50 between the two sides
  const perSideUsdt = capitalNum / 2;
  const perSideBase = price > 0 ? perSideUsdt / price : 0;
  const maxSteps = tradeSize > 0 && capitalNum > 0 ? Math.max(1, Math.floor(perSideUsdt / tradeSize)) : 0;

  const longHourlyRate = parseFloat(longLimit?.hourly_rate || 0);
  const shortHourlyRate = parseFloat(shortLimit?.hourly_rate || 0);
  const totalLiabLong = tradeSize * maxSteps;                       // USDT
  const totalLiabShort = price > 0 ? (tradeSize / price) * maxSteps : 0; // base
  const dailyLong = totalLiabLong * longHourlyRate * 24;
  const dailyShort = totalLiabShort * shortHourlyRate * 24;

  const buyTarget = parseFloat(s2.buyTarget) || 0;
  const buyConfirm = parseFloat(s2.buyConfirmation) || 0;
  const sellTarget = parseFloat(s2.sellTarget) || 0;
  const sellConfirm = parseFloat(s2.sellConfirmation) || 0;

  const errors = [];
  if (s2.submitted) {
    if (buyTarget <= 0) errors.push("Buy Target cannot be blank. Min. Buy Target = 0.01%");
    else if (buyConfirm >= buyTarget) errors.push(`Buy Confirmation is too large. Max Buy Confirmation = ${buyTarget}%`);
    if (buyConfirm <= 0) errors.push("Buy Confirmation cannot be blank. Min. Buy Confirmation = 0.01%");
    if (sellTarget <= 0) errors.push("Sell Target cannot be blank. Min. Sell Target = 0.01%");
    else if (sellConfirm >= sellTarget) errors.push(`Sell Confirmation is too large. Max Sell Confirmation = ${sellTarget}%`);
    if (sellConfirm <= 0) errors.push("Sell Confirmation cannot be blank. Min. Sell Confirmation = 0.01%");
  }

  return (
    <div className="space-y-4">
      {/* Borrowable summary — total, then the per-side borrowable behind it */}
      <div className="bg-[#F6F7F8] rounded-[10px] px-3 py-3">
        <p className="text-sm text-[#7B7B7B]">Borrowable</p>
        <p className="text-sm font-bold text-[#1C1C1C]">{fmt(totalBorrowUsdt)} USDT</p>
        {availShortBase > 0 && (
          <p className="text-[10px] text-[#9CA3AF] mt-0.5 break-words">
            {fmt(availUsdt)} USDT · {fmt(availShortBase, 2)} {baseCcy}
            {price > 0 && ` (=${fmt(availShortBase * price)} USDT)`}
          </p>
        )}
      </div>

      {/* Trading capital + slider */}
      <div>
        <FieldLabel>Trading capital after leverage</FieldLabel>
        <SuffixInput suffix="USDT" value={s2.tradingCapital} onChange={set("tradingCapital")} />

        {/* Mobile/tablet: pill buttons */}
        <div className="flex gap-1.5 mt-2 tablet:hidden">
          {[25, 50, 75, 100].map((pct) => {
            const isActive = totalBorrowUsdt > 0 && Math.round((capitalNum / totalBorrowUsdt) * 100) === pct;
            return (
              <button
                key={pct}
                type="button"
                onClick={() => setS2((p) => ({ ...p, tradingCapital: ((totalBorrowUsdt * pct) / 100).toFixed(2) }))}
                className={`flex-1 py-1.5 rounded-full text-[11px] font-medium border transition-colors ${
                  isActive
                    ? "bg-[#403297] text-white border-[#403297]"
                    : "bg-white text-[#21272A] border-gray-200 hover:border-[#403297]"
                }`}
              >
                {pct}%
              </button>
            );
          })}
        </div>

        {/* Desktop/tablet: slider */}
        <div className="mt-3 hidden tablet:block">
          <div className="relative flex items-center h-4">
            <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-1 rounded-full bg-[#F4F4F6] pointer-events-none z-[1]" />
            <div
              className="absolute left-0 top-1/2 -translate-y-1/2 h-1 rounded-full bg-[#403297] pointer-events-none z-[2]"
              style={{ width: `calc(8px + (100% - 16px) * ${Math.min(100, Math.max(0, sliderPct)) / 100})` }}
            />
            <div className="absolute left-2 right-2 top-1/2 -translate-y-1/2 flex justify-between pointer-events-none z-[3]">
              {[0, 25, 50, 75, 100].map((p) => (
                <span key={p} className="w-2 h-2 rounded-full bg-[#D1D1D1] border-2 border-white" />
              ))}
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={Math.min(100, Math.max(0, sliderPct))}
              onChange={handleSlider}
              className="range-thumb-hollow w-full relative z-10"
            />
          </div>
          <div className="flex justify-between text-xs text-[#7B7B7B] mt-1 px-2">
            {["0%", "25%", "50%", "75%", "100%"].map((l) => <span key={l}>{l}</span>)}
          </div>
        </div>
      </div>

      {/* Trade size */}
      <div>
        <FieldLabel hint>Trade size after leverage</FieldLabel>
        <SuffixInput suffix="USDT" value={s2.tradeSize} onChange={set("tradeSize")} />
      </div>

      {/* Capital split */}
      <div>
        <div className="bg-[#F6F7F8] rounded-lg py-2 text-center mb-3">
          <span className="text-[11px] font-semibold text-[#7B7B7B] tracking-wide">
            CAPITAL SPLIT <InfoIcon />
          </span>
        </div>

        {/* one bordered container; side-by-side from tablet, stacked on mobile */}
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <div className="grid grid-cols-1 divide-y divide-gray-200 tablet:grid-cols-2 tablet:divide-y-0 tablet:divide-x">
            <SplitColumn
              side="LONG"
              capitalLabel={capitalNum > 0 ? `${fmt(perSideUsdt)} USDT` : "- USDT"}
              totalLiability={maxSteps > 0 ? `${fmt(totalLiabLong)} USDT` : "-"}
              dailyInterest={maxSteps > 0 ? `${fmt(dailyLong, 6)} USDT` : "- USDT"}
              maxSteps={maxSteps > 0 ? maxSteps : "0"}
            />
            <SplitColumn
              side="SHORT"
              capitalLabel={capitalNum > 0 && price > 0 ? `${fmt(perSideBase, 2)} ${baseCcy}` : `- ${baseCcy}`}
              equivalent={capitalNum > 0 && price > 0 ? `Equivalent to ${fmt(perSideUsdt)} USDT` : null}
              totalLiability={maxSteps > 0 ? `${fmt(totalLiabShort, 2)} ${baseCcy}` : "-"}
              dailyInterest={maxSteps > 0 ? `${fmt(dailyShort, 6)} ${baseCcy}` : `- ${baseCcy}`}
              maxSteps={maxSteps > 0 ? maxSteps : "0"}
            />
          </div>
        </div>
      </div>

      {/* Bot Settings */}
      <div>
        <p className="text-sm font-semibold text-[#1C1C1C] mb-2">Bot Settings <InfoIcon /></p>
        {errors.length > 0 && (
          <div className="space-y-1.5 mb-2">
            {errors.map((err, i) => (
              <Alert key={i} status="error" alertStyle="lighter" size="small" dismissible={false} message={err} />
            ))}
          </div>
        )}
        <div className="bg-[#F6F7F8] rounded-2xl p-2.5">
          <div className="grid grid-cols-1 tablet:grid-cols-2 gap-4">
            <div>
              <FieldLabel hint error={s2.submitted && buyTarget <= 0}>Buy Target</FieldLabel>
              <SuffixInput suffix="%" value={s2.buyTarget} onChange={set("buyTarget")} step="0.01" error={s2.submitted && buyTarget <= 0} />
            </div>
            <div>
              <FieldLabel hint error={s2.submitted && (buyConfirm <= 0 || buyConfirm >= buyTarget)}>Buy Confirmation</FieldLabel>
              <SuffixInput suffix="%" value={s2.buyConfirmation} onChange={set("buyConfirmation")} step="0.01" error={s2.submitted && (buyConfirm <= 0 || buyConfirm >= buyTarget)} />
            </div>
            <div>
              <FieldLabel hint error={s2.submitted && sellTarget <= 0}>Sell Target</FieldLabel>
              <SuffixInput suffix="%" value={s2.sellTarget} onChange={set("sellTarget")} step="0.01" error={s2.submitted && sellTarget <= 0} />
            </div>
            <div>
              <FieldLabel hint error={s2.submitted && (sellConfirm <= 0 || sellConfirm >= sellTarget)}>Sell Confirmation</FieldLabel>
              <SuffixInput suffix="%" value={s2.sellConfirmation} onChange={set("sellConfirmation")} step="0.01" error={s2.submitted && (sellConfirm <= 0 || sellConfirm >= sellTarget)} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Step 3 — Confirmation ─────────────────────────────────────────────────────

function Step3({ s1, s2, tradable, longLimit, shortLimit, termsChecked, onTermsChange, showTermsError }) {
  const exchange = tradable.find((a) => String(a.id) === String(s1.credentialId))?.label || "—";
  const price = parseFloat(shortLimit?.price || longLimit?.price || 0);
  const baseCcy = longLimit?.base_ccy || shortLimit?.base_ccy || s1.symbol.toUpperCase();

  const capitalNum = parseFloat(s2.tradingCapital) || 0;
  const tradeSize = parseFloat(s2.tradeSize) || 0;
  const perSideUsdt = capitalNum / 2;
  const perSideBase = price > 0 ? perSideUsdt / price : 0;
  const maxSteps = tradeSize > 0 && capitalNum > 0 ? Math.max(1, Math.floor(perSideUsdt / tradeSize)) : 0;

  const longHourlyRate = parseFloat(longLimit?.hourly_rate || 0);
  const shortHourlyRate = parseFloat(shortLimit?.hourly_rate || 0);
  const totalLiabLong = tradeSize * maxSteps;
  const totalLiabShort = price > 0 ? (tradeSize / price) * maxSteps : 0;
  const dailyLong = totalLiabLong * longHourlyRate * 24;
  const dailyShort = totalLiabShort * shortHourlyRate * 24;

  const coinBadge = (
    <span className="w-4 h-4 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-[6px] font-bold text-white shrink-0">
      {(baseCcy || "?").slice(0, 3)}
    </span>
  );

  const infoRows = [
    { label: "Exchange", value: exchange, icon: <img src="/icon_okx.svg" alt="" className="w-4 h-4 rounded-full" /> },
    { label: "Trading Pair", value: `${s1.symbol.toUpperCase()}/USDT`, icon: coinBadge },
    { label: "Direction", value: "LONG+SHORT" },
    { label: "Borrowed currency", value: `USDT & ${baseCcy}` },
  ];

  const splitRows = [
    {
      label: "Trading capital after leverage",
      long: `${fmt(perSideUsdt)} USDT`,
      short: price > 0 ? `${fmt(perSideBase, 2)} ${baseCcy} (=${fmt(perSideUsdt)} USDT)` : "—",
    },
    { label: "Total Liability", long: `${fmt(totalLiabLong)} USDT`, short: `${fmt(totalLiabShort, 2)} ${baseCcy}` },
    { label: "Est. Daily Interest", long: `${fmt(dailyLong, 6)} USDT`, short: `${fmt(dailyShort, 6)} ${baseCcy}` },
    { label: "Trade size after leverage", long: `${fmt(tradeSize)} USDT`, short: `${fmt(tradeSize)} USDT` },
    { label: "Max Steps", long: maxSteps, short: maxSteps },
  ];

  const sharedRows = [
    ["Buy Target", `${s2.buyTarget}%`],
    ["Buy Confirmation", `${s2.buyConfirmation}%`],
    ["Sell Target", `${s2.sellTarget}%`],
    ["Sell Confirmation", `${s2.sellConfirmation}%`],
  ];

  return (
    <div className="space-y-4">
      {/* MMR warning */}
      <div className="flex items-start gap-2 bg-[#DAD6F2] text-[#302571] text-xs rounded-xl px-3 py-2.5">
        <StarLines className="w-4 h-4 shrink-0 text-[#403297]" />
        <span>
          Running margin+leverage Bot will require you to keep a maintenance margin ratio
          (MMR) above 100% to avoid liquidation. You can increase your MMR by adding fund
          ({baseCcy}) to your exchange account.
        </span>
      </div>

      {/* Trade config summary */}
      <div className="bg-[#F8F9FB] rounded-xl px-4 py-1">
        {infoRows.map(({ label, value, icon }) => (
          <div key={label} className="flex items-center justify-between py-2.5 gap-2">
            <span className="text-sm text-[#697077]">{label}</span>
            <span className="text-sm font-semibold text-[#21272A] flex items-center gap-1.5 text-right">
              {value}
              {icon}
            </span>
          </div>
        ))}
      </div>

      {/* Bot settings — per-side figures, then the grid params both sides share */}
      <div>
        <p className="text-sm font-semibold text-[#1C1C1C] mb-2">Bot Settings</p>

        {/* one continuous panel — the two groups are parted by a hairline only */}
        <div className="bg-[#F8F9FB] rounded-xl px-4 py-1">
          {/* three columns hold at every width — long values wrap rather than scroll */}
          <table className="w-full text-xs">
            <thead>
              <tr>
                <th className="text-left py-2 font-medium text-[10px] text-[#9CA3AF] uppercase tracking-wide">Detail</th>
                <th className="text-right py-2 font-medium text-[10px] text-[#9CA3AF] uppercase tracking-wide">Long</th>
                <th className="text-right py-2 font-medium text-[10px] text-[#9CA3AF] uppercase tracking-wide">Short</th>
              </tr>
            </thead>
            <tbody>
              {splitRows.map((r) => (
                <tr key={r.label}>
                  <td className="py-2 pr-2 text-[#697077] align-top">{r.label}</td>
                  <td className="py-2 px-2 text-right font-semibold text-[#21272A] align-top break-words">{r.long}</td>
                  <td className="py-2 pl-2 text-right font-semibold text-[#21272A] align-top break-words">{r.short}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="border-t border-gray-200 my-1" />

          {sharedRows.map(([label, value]) => (
            <div key={label} className="flex items-center justify-between py-2">
              <span className="text-xs text-[#697077]">{label}</span>
              <span className="text-xs font-semibold text-[#21272A]">{value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* T&C — the unticked error sits directly above the box it refers to */}
      {showTermsError && (
        <Alert
          status="error"
          alertStyle="lighter"
          size="small"
          dismissible={false}
          message="Before continue you required to tick the terms and condition"
        />
      )}

      <label className="flex items-start gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={termsChecked}
          onChange={(e) => onTermsChange(e.target.checked)}
          className="mt-0.5 w-4 h-4 rounded accent-[#302571] cursor-pointer"
        />
        <span className="text-xs text-[#21272A]">
          By activating this bot, I confirm that I accept Xenorize's Terms and Conditions and acknowledge the risks involved.
        </span>
      </label>
    </div>
  );
}

// ── Defaults ──────────────────────────────────────────────────────────────────

const S1_DEFAULTS = { credentialId: "", symbol: "", leverage: "" };
const S2_DEFAULTS = {
  tradingCapital: "",
  tradeSize: "",
  buyTarget: "1",
  buyConfirmation: "0.5",
  sellTarget: "1",
  sellConfirmation: "0.5",
  submitted: false,
};

// ── Main export ───────────────────────────────────────────────────────────────

export default function ActivateLSBotModal({ open, onClose, accounts = [], onStarted, dryRun = false }) {
  const { authedPost } = useAuthedApi();
  const tradable = accounts.filter((a) => a.is_trading_enabled);

  const [step, setStep] = useState(1);
  const [s1, setS1] = useState(S1_DEFAULTS);
  const [s2, setS2] = useState(S2_DEFAULTS);

  const [longLimit, setLongLimit] = useState(null);
  const [shortLimit, setShortLimit] = useState(null);
  const [loadingLong, setLoadingLong] = useState(false);
  const [loadingShort, setLoadingShort] = useState(false);
  const [longError, setLongError] = useState(null);
  const [shortError, setShortError] = useState(null);

  const [termsChecked, setTermsChecked] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const prevOpen = useRef(false);
  useEffect(() => {
    if (open && !prevOpen.current) {
      setStep(1);
      setS1({ ...S1_DEFAULTS, credentialId: tradable[0]?.id || "" });
      setS2({ ...S2_DEFAULTS });
      setLongLimit(null); setShortLimit(null);
      setLongError(null); setShortError(null);
      setSubmitError(null);
      setTermsChecked(false);
      setShowTermsError(false);
    }
    prevOpen.current = open;
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch LONG + SHORT limits
  useEffect(() => {
    if (!s1.credentialId || !s1.symbol || s1.symbol.length < 2 || !s1.leverage) {
      setLongLimit(null); setShortLimit(null);
      return;
    }
    let alive = true;
    const payload = { symbol: s1.symbol.toUpperCase() + "USDT", credential_id: s1.credentialId, leverage: Number(s1.leverage) };
    const t = setTimeout(async () => {
      setLoadingLong(true); setLoadingShort(true);
      setLongError(null); setShortError(null);
      const [longRes, shortRes] = await Promise.allSettled([
        authedPost("/api/borrow/check-limit/", { ...payload, direction: "LONG", ...(dryRun && { dry_run: true }) }),
        authedPost("/api/borrow/check-limit/", { ...payload, direction: "SHORT", ...(dryRun && { dry_run: true }) }),
      ]);
      if (!alive) return;
      if (longRes.status === "fulfilled") { setLongLimit(longRes.value.data); }
      else { setLongError(longRes.reason?.response?.data?.error || "Failed to load LONG"); setLongLimit(null); }
      if (shortRes.status === "fulfilled") { setShortLimit(shortRes.value.data); }
      else { setShortError(shortRes.reason?.response?.data?.error || "Failed to load SHORT"); setShortLimit(null); }
      setLoadingLong(false); setLoadingShort(false);
    }, 700);
    return () => { alive = false; clearTimeout(t); };
  }, [s1.credentialId, s1.symbol, s1.leverage]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── derived ─────────────────────────────────────────────────────────────
  const price = parseFloat(shortLimit?.price || longLimit?.price || 0);
  const leverage = Number(s1.leverage) || 1;
  const capitalNum = parseFloat(s2.tradingCapital) || 0;
  const tradeSize = parseFloat(s2.tradeSize) || 0;
  const perSideUsdt = capitalNum / 2;
  const maxSteps = tradeSize > 0 && capitalNum > 0 ? Math.max(1, Math.floor(perSideUsdt / tradeSize)) : 0;
  const collateral = leverage > 0 ? tradeSize / leverage : tradeSize;

  const buyTarget = parseFloat(s2.buyTarget) || 0;
  const buyConfirm = parseFloat(s2.buyConfirmation) || 0;
  const sellTarget = parseFloat(s2.sellTarget) || 0;
  const sellConfirm = parseFloat(s2.sellConfirmation) || 0;

  // Mirrors Step1's longAvail/shortAvailBase logic. LS needs both sides to
  // work, so both must have non-zero borrowable before Continue is allowed.
  const longAvail = parseFloat(longLimit?.available_loan_usdt || 0);
  const shortAvailBase = parseFloat(shortLimit?.available_loan || 0);

  const canStep1 = !!s1.credentialId && s1.symbol.length >= 2 && !!s1.leverage &&
    !loadingLong && !loadingShort && !!longLimit && !!shortLimit &&
    longAvail > 0 && shortAvailBase > 0;

  const step2Valid =
    capitalNum > 0 && tradeSize > 0 && maxSteps > 0 &&
    buyTarget > 0 && buyConfirm > 0 && buyConfirm < buyTarget &&
    sellTarget > 0 && sellConfirm > 0 && sellConfirm < sellTarget;

  const canPrimary = step === 1 ? canStep1 : step === 2 ? step2Valid : !submitting;

  const handleNext = async () => {
    if (step === 2) {
      setS2((p) => ({ ...p, submitted: true }));
      if (!step2Valid) return;
    }
    if (step < 3) { setStep((s) => s + 1); return; }

    if (!termsChecked) { setShowTermsError(true); return; }
    setShowTermsError(false);
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await authedPost("/api/borrow/start-ls6/", {
        symbol:                s1.symbol.toUpperCase() + "USDT",
        leverage,
        collateral_per_trade:  collateral,
        initial_capital:       capitalNum,
        number_of_grids_long:  maxSteps,
        number_of_grids_short: maxSteps,
        grid_width:            buyTarget,
        buy_flg_return:        buyConfirm,
        grid_width_exit:       sellTarget,
        exit_flg_return:       sellConfirm,
        credential_id:         s1.credentialId,
        dry_run:               dryRun,
        use_sl:                false,
        sl_margin_loss_pct:    0.5,
        ha_interval:           "1D",
        doji_threshold_pct:    0.3,
      });
      onStarted?.(res.data);
      onClose();
    } catch (err) {
      setSubmitError(err.response?.data?.error || "Failed to start LS bot");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Xenoarc-Long and Short Activation"
      width="570px"
      footer={
        <div className="flex items-center gap-3">
          <button
            onClick={step === 1 ? onClose : () => setStep((s) => s - 1)}
            className="flex-1 py-2.5 rounded-xl border border-[#EAEAEA] text-sm font-medium text-[#333333] hover:bg-gray-50 transition-colors"
          >
            {step === 1 ? "Cancel" : "Back"}
          </button>
          <button
            onClick={handleNext}
            disabled={(step !== 3 && !canPrimary) || (step === 3 && !termsChecked && !submitting)}
            className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-all
              ${((step !== 3 && !canPrimary) || (step === 3 && !termsChecked && !submitting))
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-[#302571] text-white hover:bg-[#241c5c]"}`}
          >
            {step === 3 ? (submitting ? "Starting…" : "Confirm") : "Continue"}
          </button>
        </div>
      }
    >
      <Stepper step={step} />

      {/* ponytail: gated on `open`, see ActivateBotModal.jsx for why */}
      {open && step === 1 && (
        <Step1
          s1={s1} setS1={setS1} tradable={tradable}
          longLimit={longLimit} shortLimit={shortLimit}
          loadingLong={loadingLong} loadingShort={loadingShort}
          longError={longError} shortError={shortError}
        />
      )}
      {open && step === 2 && (
        <Step2 s1={s1} s2={s2} setS2={setS2} longLimit={longLimit} shortLimit={shortLimit} />
      )}
      {open && step === 3 && (
        <Step3
          s1={s1} s2={s2} tradable={tradable}
          longLimit={longLimit} shortLimit={shortLimit}
          termsChecked={termsChecked}
          onTermsChange={(v) => { setTermsChecked(v); if (v) setShowTermsError(false); }}
          showTermsError={showTermsError}
        />
      )}
      {submitError && <p className="text-xs text-red-500 mt-3">{submitError}</p>}
    </Drawer>
  );
}
